import { useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { DialogFooter, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import InputField from '@savvy-kit/patterns/input-field';
import { toast } from 'sonner';
import { TrackedButton as TrackedButtonV2 } from 'src/components/tracked';
import TrackedButton from 'src/components/tracked/tracked-button';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { ORION_URL } from 'src/constants/logo-urls/financial-institution-logo';
import { graphql } from 'src/gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';
import { providerImgSrc } from 'src/utils/image';
import { DatadogRumAction } from 'src/utils/logging';

import type { AddSecondaryPortfolioPayload } from '@savvy/constants/generated/gql-types';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

type AddSecondaryPortfolioButtonProps = {
  readonly savvyUserId: string;
};

const ADD_SECONDARY_PORTFOLIO_MUTATION = graphql(/* GraphQL */ `
  mutation AddSecondaryPortfolio($input: AddSecondaryPortfolioInput!) {
    addSecondaryPortfolio(input: $input) {
      errorMessage
      success
    }
  }
`);

function AddSecondaryPortfolioButton({ savvyUserId }: AddSecondaryPortfolioButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [secondaryPortfolioId, setSecondaryPortfolioId] = useState('');
  const [addSecondaryPortfolio] = useMutationWithErrorLogging<{ addSecondaryPortfolio: AddSecondaryPortfolioPayload }>(
    ADD_SECONDARY_PORTFOLIO_MUTATION,
  );

  const handleSubmit = () => {
    runQuery();

    datadogRum.addAction(DatadogRumAction.AddSecondaryPortfolio, {
      savvyUserId,
      secondaryPortfolioId,
    });
    setIsDialogOpen(false);
    setSecondaryPortfolioId('');
  };

  const runQuery = async (additionalParams = {}) => {
    const toastId = toast.loading('Connecting...');
    try {
      const { data } = await addSecondaryPortfolio({
        variables: {
          input: {
            userId: savvyUserId,
            secondaryPortfolioId,
            ...additionalParams,
          },
        },
      });

      if (data?.addSecondaryPortfolio.success) {
        toast.success('Success! The secondary portfolio should show up in a few moments.', {
          id: toastId,
          style: { paddingLeft: '18px' },
          duration: 6000,
        });
      } else {
        toast.error(data?.addSecondaryPortfolio.errorMessage || 'An error occurred', { id: toastId });
      }
    } catch (error) {
      handleMutationError(error, 'An unexpected error occurred', { id: toastId });
    }
  };

  return (
    <>
      <TrackedButtonV2
        variant="secondary"
        elementName="admin-action-buttons-add-secondary-portfolio-button"
        onClick={() => setIsDialogOpen(true)}
        icon={
          // eslint-disable-next-line @next/next/no-img-element -- providerImgSrc returns a tiny provider icon URL for button chrome
          <img src={providerImgSrc(ORION_URL)} alt="Orion" className="size-4 bg-inherit" />
        }
        iconPosition="right"
      >
        Add Secondary Portfolio
      </TrackedButtonV2>

      <TrackedDialog
        elementName="add-secondary-portfolio-dialog"
        open={isDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && setIsDialogOpen(false)}
        className="sm:max-w-[524px]"
      >
        <DialogHeader>
          <DialogTitle>Specify the Orion Household ID for the secondary portfolio</DialogTitle>
        </DialogHeader>
        <InputField
          id="secondary-portfolio-orion-household-id"
          autoFocus
          label="Orion Household ID"
          value={secondaryPortfolioId}
          onChange={(e) => setSecondaryPortfolioId(e.target.value)}
        />
        <DialogFooter>
          <TrackedButton
            elementName="add-secondary-portfolio-dialog-cancel-button"
            onClick={() => {
              setIsDialogOpen(false);
            }}
          >
            Cancel
          </TrackedButton>
          <TrackedButton
            elementName="add-secondary-portfolio-dialog-submit-button"
            variant="default"
            onClick={() => {
              handleSubmit();
              setIsDialogOpen(false);
            }}
          >
            Submit
          </TrackedButton>
        </DialogFooter>
      </TrackedDialog>
    </>
  );
}

export default AddSecondaryPortfolioButton;
